import { useEffect, useState, useCallback } from "react";
import { api, apiError, timeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SignIn, SignOut, Prohibit, Plus, Printer, NotePencil, UserCircle } from "@phosphor-icons/react";
import { Link, useSearchParams } from "react-router-dom";
import { Photo } from "@/components/Photo";
import { StudentAvatar } from "@/components/StudentAvatar";
import { ObservationDialog } from "@/components/ObservationDialog";

const ACTIVITIES = ["meal", "nap", "diaper", "note", "photo", "incident", "medication", "kudos", "health"];

const stateChip = {
  in: "bg-emerald-50 text-emerald-700 border-emerald-200",
  out: "bg-slate-100 text-slate-600 border-slate-200",
  absent: "bg-red-50 text-red-600 border-red-200",
  none: "bg-amber-50 text-amber-700 border-amber-200",
};
const stateLabel = { in: "Checked in", out: "Checked out", absent: "Absent", none: "Not arrived" };

export default function Attendance() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [day, setDay] = useState(params.get("day") || new Date().toISOString().slice(0, 10));
  const [filter, setFilter] = useState(params.get("filter") || "all");
  const [guardian, setGuardian] = useState("");
  const [act, setAct] = useState({ open: false, student: null, type: "meal", detail: "", staff_only: false });
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feed, setFeed] = useState([]);
  const [absent, setAbsent] = useState(null);
  const [obs, setObs] = useState(null);

  const load = useCallback(async () => {
    const [{ data: r }, { data: a }] = await Promise.all([
      api.get("/attendance/roster", { params: { day } }),
      api.get("/activities", { params: { day } }),
    ]);
    setData(r);
    setFeed(a);
  }, [day]);

  useEffect(() => { load(); }, [load]);
  const shownStudents = (data?.students || []).filter((s) => {
    if (filter === "all") return true;
    if (filter === "scheduled") return s.scheduled_today;
    if (filter === "late_arrival") return s.late_arrival_minutes > 0;
    if (filter === "late_pickup") return s.late_pickup_minutes > 0;
    if (filter === "excused") return s.state === "absent" && s.events.at(-1)?.excused === true;
    if (filter === "unexcused") return s.state === "absent" && s.events.at(-1)?.excused === false;
    return s.state === filter;
  });

  const record = async (student_id, kind, extra = {}) => {
    try {
      await api.post("/attendance", { student_id, kind, guardian, ...extra });
      toast.success(`Recorded ${stateLabel[kind].toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const logActivity = async () => {
    setSaving(true);
    try {
      if (act.type === "photo") {
        if (!photoFile) {
          toast.error("Choose a photo to upload");
          setSaving(false);
          return;
        }
        const fd = new FormData();
        fd.append("student_id", act.student.id);
        fd.append("caption", act.detail);
        fd.append("staff_only", act.staff_only);
        fd.append("file", photoFile);
        await api.post("/activities/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Photo shared with the family");
      } else {
        await api.post("/activities", {
          student_id: act.student.id, type: act.type, detail: act.detail, staff_only: act.staff_only,
        });
        toast.success("Activity logged");
      }
      setPhotoFile(null);
      setAct({ ...act, open: false, detail: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div data-testid="attendance-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Attendance & Daily Care</p>
      <div className="mt-3 flex flex-wrap gap-4 items-end justify-between">
        <h1 className="text-4xl font-bold tracking-tight">Check-in board</h1>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/attendance-log">
            <Button variant="outline" className="rounded-full font-bold" data-testid="open-attendance-log">
              Full check-in / check-out history
            </Button>
          </Link>
          <Link to="/admin/classroom">
            <Button variant="outline" className="rounded-full font-bold" data-testid="open-classroom-tablet">
              Classroom tablet view
            </Button>
          </Link>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-4 items-end">
        <div>
          <Label className="text-sm font-bold">Date</Label>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
                 className="mt-2 h-11 rounded-xl w-44" data-testid="attendance-date" />
        </div>
        <div><Label className="text-sm font-bold">Show</Label><Select value={filter} onValueChange={(v) => { setFilter(v); setParams({ day, filter: v }); }}><SelectTrigger className="mt-2 h-11 rounded-xl w-52"><SelectValue /></SelectTrigger><SelectContent>{[["all", "All students"], ["scheduled", "Scheduled"], ["in", "Checked in"], ["out", "Checked out"], ["absent", "Absent"], ["excused", "Excused absences"], ["unexcused", "Unexcused absences"], ["late_arrival", "Late arrivals"], ["late_pickup", "Late pickups"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
        <div>
          <Label className="text-sm font-bold">Guardian dropping off / picking up</Label>
          <Input value={guardian} onChange={(e) => setGuardian(e.target.value)} placeholder="e.g. Jamie Parent"
                 className="mt-2 h-11 rounded-xl w-64" data-testid="attendance-guardian" />
        </div>
      </div>

      {!data ? (
        <p className="mt-10 text-muted-foreground">Loading…</p>
      ) : (
        <div className="mt-8 grid lg:grid-cols-[1.4fr,1fr] gap-6">
          <div className="space-y-3" data-testid="roster-list">
            {shownStudents.length === 0 && <p className="text-muted-foreground">No students match this dashboard filter.</p>}
            {shownStudents.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-4 items-center"
                   data-testid={`roster-row-${s.id}`}>
                <StudentAvatar student={{ first_name: s.name.split(" ")[0], last_name: s.name.split(" ").slice(1).join(" "), photo_path: s.photo_path }} size="sm" />
                <div className="min-w-0 flex-1">
                  <Link to={`/admin/students/${s.id}`} className="font-bold hover:text-[#3B82F6]" data-testid={`roster-name-${s.id}`}>{s.name}</Link>
                  <p className="text-xs text-muted-foreground mt-1 font-semibold">
                    {s.room} · {s.program} · Code {s.checkin_code}{!s.scheduled_today && " · not scheduled today"}
                  </p>
                </div>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${stateChip[s.state]}`}
                      data-testid={`roster-state-${s.id}`}>
                  {stateLabel[s.state]}{s.last_at ? ` · ${timeOf(s.last_at)}` : ""}
                </span>
                {s.late_arrival_minutes > 0 && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700" data-testid={`late-arrival-${s.id}`}>{s.late_arrival_minutes} min late</span>}
                {s.late_pickup_minutes > 0 && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-600" data-testid={`late-pickup-${s.id}`}>Picked up {s.late_pickup_minutes} min late</span>}
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => record(s.id, "in")}
                          className="rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold" data-testid={`checkin-${s.id}`}>
                    <SignIn size={16} className="mr-1" /> In
                  </Button>
                  <Button size="sm" onClick={() => record(s.id, "out")}
                          className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid={`checkout-${s.id}`}>
                    <SignOut size={16} className="mr-1" /> Out
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setAbsent({ student: s, reason: "", excused: "none" })}
                          className="rounded-full font-bold" data-testid={`absent-${s.id}`} aria-label="Mark absent">
                    <Prohibit size={16} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setObs({ id: s.id, first_name: s.name.split(" ")[0], last_name: s.name.split(" ").slice(1).join(" ") })}
                          className="rounded-full font-bold" data-testid={`observe-${s.id}`} aria-label="Add observation">
                    <NotePencil size={16} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setAct({ open: true, student: s, type: "meal", detail: "", staff_only: false })}
                          className="rounded-full font-bold" data-testid={`log-activity-${s.id}`}>
                    <Plus size={16} />
                  </Button>
                  <Link to={`/admin/daysheet/${s.id}?day=${day}`}>
                    <Button size="sm" variant="outline" className="rounded-full font-bold"
                            data-testid={`day-sheet-${s.id}`}>
                      <Printer size={16} />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-border p-6" data-testid="activity-feed">
            <h2 className="font-display text-xl font-semibold">Activity feed</h2>
            <div className="mt-5 space-y-4 max-h-[32rem] overflow-y-auto">
              {feed.length === 0 && <p className="text-sm text-muted-foreground">Nothing logged yet today.</p>}
              {feed.map((a) => (
                <div key={a.id} className="border-b border-border pb-3">
                  <p className="text-sm font-bold">
                    {a.student_name} · <span className="capitalize text-[#3B82F6]">{a.type}</span>
                    {a.staff_only && <span className="ml-2 text-xs text-[#EF4444]">staff only</span>}
                  </p>
                  {a.photo_path && (
                    <Photo path={a.photo_path} alt={a.detail || "Classroom photo"}
                           className="mt-2 rounded-xl w-full h-40 object-cover"
                           testid={`feed-photo-${a.id}`} />
                  )}
                  {a.detail && <p className="text-sm text-muted-foreground mt-1">{a.detail}</p>}
                  <p className="text-xs text-muted-foreground mt-1">{timeOf(a.at)} · {a.staff}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Dialog open={Boolean(absent)} onOpenChange={(o) => !o && setAbsent(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="absent-dialog">
          <DialogHeader><DialogTitle className="font-display">Mark absent — {absent?.student?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-bold">Reason</Label>
              <Input value={absent?.reason || ""} onChange={(e) => setAbsent({ ...absent, reason: e.target.value })}
                     placeholder="Illness, family trip, appointment…" className="mt-2 h-11 rounded-xl" data-testid="absent-reason" />
            </div>
            <div>
              <Label className="text-sm font-bold">Excused?</Label>
              <Select value={absent?.excused || "none"} onValueChange={(v) => setAbsent({ ...absent, excused: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="absent-excused"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not classified yet</SelectItem>
                  <SelectItem value="yes">Excused</SelectItem>
                  <SelectItem value="no">Unexcused</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={async () => { await record(absent.student.id, "absent", { reason: absent.reason, excused: absent.excused === "none" ? null : absent.excused === "yes" }); setAbsent(null); }}
                    className="w-full h-11 rounded-full bg-[#EF4444] hover:bg-red-600 font-bold" data-testid="absent-save">Mark absent</Button>
          </div>
        </DialogContent>
      </Dialog>
      {obs && <ObservationDialog open onOpenChange={(o) => !o && setObs(null)} student={obs} onSaved={load} />}

      <Dialog open={act.open} onOpenChange={(o) => setAct({ ...act, open: o })}>
        <DialogContent className="rounded-3xl" data-testid="activity-dialog">
          <DialogHeader><DialogTitle className="font-display">Log activity — {act.student?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-bold">Type</Label>
              <Select value={act.type} onValueChange={(v) => setAct({ ...act, type: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="activity-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITIES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {act.type === "photo" && (
              <div>
                <Label className="text-sm font-bold">Photo</Label>
                <Input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                       className="mt-2 h-11 rounded-xl" data-testid="activity-photo-file" />
                <p className="mt-2 text-xs text-muted-foreground">JPG, PNG, GIF, WEBP or HEIC up to 10 MB.</p>
              </div>
            )}
            <div>
              <Label className="text-sm font-bold">{act.type === "photo" ? "Caption" : "Details"}</Label>
              <Textarea value={act.detail} onChange={(e) => setAct({ ...act, detail: e.target.value })}
                        className="mt-2 rounded-xl" placeholder="Ate all of lunch, napped 12:30–2:00…"
                        data-testid="activity-detail" />
            </div>
            <label className="flex items-center gap-3 text-sm font-semibold">
              <input type="checkbox" checked={act.staff_only} onChange={(e) => setAct({ ...act, staff_only: e.target.checked })}
                     data-testid="activity-staff-only" /> Staff only (hidden from parents)
            </label>
            <Button onClick={logActivity} disabled={saving}
                    className="w-full h-11 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="activity-save">{saving ? "Saving…" : "Save activity"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
